import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Text } from '@mantine/core';
import type { Zone } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from '../pages/EntityPage.module.css';

/**
 * «Експорт CSV» — кнопка, якої не було ніде.
 *
 * Мокап малює три різні («Експорт CSV», «ЕКСПОРТ У РОБОТУ», «МАСОВИЙ ЕКСПОРТ»), і всі три
 * ведуть в одне: віддати оператору те, що він бачить, у вигляді, який відкриється в таблиці.
 *
 * Тягне запит **на клік**, а не тримає його відкритим: вивантаження реєстру — це весь реєстр
 * у памʼяті, і робити це на кожному відкритті екрана заради кнопки, яку тиснуть раз на день,
 * означало б платити за неї постійно.
 *
 * Файл віддається через `Blob` і посилання, яке клацає сам код. Це не обхід — сервер повертає
 * готовий CSV разом з іменем файлу, і саме сервер вирішує, що в нього входить: зона й ГЕО-скоуп
 * звужують експорт так само, як реєстр.
 */

export interface ExportButtonProps {
  /** Ключ процедури в неймспейсі `export`. */
  dataset: 'sites' | 'brands' | 'keywords' | 'guestPosts' | 'donors' | 'anchors';
  /** Зона, у якій живе цей набір: кнопка питає матрицю сама. */
  zone: Zone;
  /** «Експортувати вибране» — та сама вибірка, що на екрані. Порожньо = увесь реєстр. */
  ids?: string[];
  label?: string;
}

export function ExportButton({
  dataset,
  zone,
  ids,
  label = 'Експорт CSV',
}: ExportButtonProps): React.JSX.Element | null {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canRead = useCan(zone, 'read');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (!canRead) return null;

  const selected = ids !== undefined && ids.length > 0;

  const download = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      /*
       * Явний перебір, а не `trpc.export[dataset]`.
       *
       * Динамічний доступ статично невидимий: гейт «кожна процедура має викликача» рахував
       * усі шість експортів сиротами, хоч вони викликаються. Гейт мав рацію в головному —
       * так само його не побачив би й компілятор, і будь-хто, хто шукає викликача пошуком.
       */
      const input = selected ? { ids } : {};
      const options =
        dataset === 'sites'
          ? trpc.export.sites.queryOptions(input)
          : dataset === 'brands'
            ? trpc.export.brands.queryOptions(input)
            : dataset === 'keywords'
              ? trpc.export.keywords.queryOptions(input)
              : dataset === 'guestPosts'
                ? trpc.export.guestPosts.queryOptions(input)
                : dataset === 'donors'
                  ? trpc.export.donors.queryOptions(input)
                  : trpc.export.anchors.queryOptions(input);

      const result = await queryClient.fetchQuery(options);

      const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      link.click();
      // Без відкликання посилання блоб живе до перезавантаження сторінки.
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="compact-xs"
        variant="default"
        loading={busy}
        data-testid={`export-${dataset}`}
        onClick={() => {
          void download();
        }}
      >
        {selected ? `${label} · ${String(ids.length)}` : label}
      </Button>
      {error === null ? null : (
        <Text className={classes.danger} data-testid={`export-error-${dataset}`} role="alert">
          {publicMessageOf(error)}
        </Text>
      )}
    </>
  );
}
